import React, { useState } from 'react';
import { Activity, Award, ChevronDown, ChevronUp } from 'lucide-react';
import { ExportMenu } from '../../ExportMenu';
import { BasicHistoryChart } from '../BasicHistoryChart';
import type { CarrierProfile as CarrierProfileType } from '../../../types/api';
import type { BASICScore } from '../CarrierTabs.types';

// Helper function to decode HTML entities in text
const decodeHTMLEntities = (text: string): string => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

export interface BasicsProps {
  profile: CarrierProfileType;
  basics: BASICScore[];
  basicsLoading?: boolean;
}

const Basics: React.FC<BasicsProps> = ({ profile, basics, basicsLoading = false }) => {
  const [expandedBasicId, setExpandedBasicId] = useState<string | null>(null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xl)' }}>
      {/* Export Buttons */}
      <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-sm)' }}>
        <ExportMenu
          dataType="basics"
          data={basics}
          carrierName={profile?.legalName}
          dotNumber={profile?.dotNumber}
          buttonText="Export BASIC Scores"
          size="sm"
        />
      </div>

      {/* Info Banner */}
      <div className="card-modern fade-in-up" style={{ borderLeft: '4px solid var(--color-primary)' }}>
        <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'flex-start' }}>
          <Award style={{ width: 24, height: 24, color: 'var(--color-primary)', flexShrink: 0 }} />
          <div>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--color-gray-900)', marginBottom: 'var(--space-sm)' }}>
              FMCSA BASIC Safety Categories
            </h4>
            <p className="text-muted" style={{ lineHeight: 1.6 }}>
              Behavior Analysis and Safety Improvement Categories (BASIC) measure carrier performance.
              Percentiles show ranking vs. all carriers - lower is better. Scores marked "Not Public" are hidden by FMCSA.
            </p>
          </div>
        </div>
      </div>

      {basicsLoading ? (
        <div className="card-modern fade-in-up" style={{ textAlign: 'center', padding: 'var(--space-xl)' }}>
          <Activity style={{ width: 32, height: 32, color: 'var(--color-primary)', animation: 'spin 1s linear infinite' }} />
          <p style={{ marginTop: 'var(--space-md)', color: 'var(--color-gray-600)' }}>Loading BASIC scores...</p>
        </div>
      ) : basics.length > 0 ? (
        <>
          {basics.map((item, idx) => {
            const basic = item.basic || item;
            const basicsPercentileStr = String(basic.basicsPercentile ?? '');
            const isPublic = basicsPercentileStr !== 'Not Public' && basicsPercentileStr !== '';
            const percentile = isPublic ? parseFloat(basicsPercentileStr) : null;
            const thresholdStr = String(basic.basicsViolationThreshold ?? '0');
            const threshold = parseFloat(thresholdStr) || 0;
            const exceeded = percentile && percentile > threshold;

            // Check if this is an estimated score from backend
            const isBackendEstimated = basic.estimated === true;

            // FEATURE 3: SMS Percentile Estimation (client-side fallback)
            let estimatedPercentile: number | null = null;
            let isEstimated = isBackendEstimated;

            if (isBackendEstimated) {
              // Backend provided estimation
              estimatedPercentile = isPublic ? percentile : null;
            } else if (!isPublic && basic.measureValue && threshold > 0) {
              // Client-side estimation (fallback)
              const measureValueStr = String(basic.measureValue ?? '0');
              const measureValue = parseFloat(measureValueStr) || 0;
              const ratio = measureValue / threshold;
              if (ratio < 1) {
                estimatedPercentile = Math.round(ratio * 60);
              } else {
                estimatedPercentile = Math.round(60 + (ratio - 1) * 40);
              }
              estimatedPercentile = Math.min(100, Math.max(0, estimatedPercentile));
              isEstimated = true;
            }

            const displayPercentile = isPublic ? percentile : estimatedPercentile;
            const displayExceeded = displayPercentile && displayPercentile > threshold;

            // Safe access to basicsType properties
            const basicsType = basic.basicsType as { basicsId?: number; basicsShortDesc?: string; basicsCode?: string } | undefined;
            const basicsId = basicsType?.basicsId ?? idx;
            const basicsShortDesc = basicsType?.basicsShortDesc ?? '';
            const basicsCode = basicsType?.basicsCode ?? 'N/A';
            // basicsCode is sometimes the human name (e.g. "Unsafe Driving") rather
            // than "BASIC-11" depending on data source; basicsId is always the
            // numeric 11-17, so build the canonical id from that for the history API.
            const canonicalBasicId = `BASIC-${basicsId}`;
            const isExpanded = expandedBasicId === canonicalBasicId;

            return (
              <div key={basicsId} className="card-modern fade-in-up" style={{ animationDelay: `${idx * 0.05}s` }}>
                <div
                  onClick={() => setExpandedBasicId(isExpanded ? null : canonicalBasicId)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: 'var(--space-lg)',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                  title="Click to view weekly history"
                >
                  {/* Category Name */}
                  <div style={{ gridColumn: 'span 2', minWidth: '200px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-sm)' }}>
                      <Award size={20} style={{ color: 'var(--color-primary)' }} />
                      <h4 className="heading-3" style={{ margin: 0 }}>
                        {basicsShortDesc ? decodeHTMLEntities(basicsShortDesc) : basicsCode}
                      </h4>
                      {isExpanded ? <ChevronUp size={16} style={{ color: 'var(--color-gray-400)' }} /> : <ChevronDown size={16} style={{ color: 'var(--color-gray-400)' }} />}
                    </div>
                    <p className="text-muted" style={{ fontSize: '0.8125rem' }}>
                      {basicsCode}
                    </p>
                  </div>

                  {/* Measure Value -- always rendered (never conditionally hidden) so every
                      BASIC's card row has the same column count; a missing/eliminated
                      measure shows "N/A" instead of silently collapsing the grid. */}
                  <div className="stat-box">
                    <div className="metric-value" style={{
                      fontSize: '1.25rem',
                      color: 'var(--color-gray-700)'
                    }}>
                      {basic.measureValue !== null && basic.measureValue !== undefined
                        ? parseFloat(String(basic.measureValue)).toFixed(2)
                        : 'N/A'}
                    </div>
                    <div className="metric-subtitle">
                      Measure
                    </div>
                  </div>

                  {/* Percentile */}
                  <div className="stat-box">
                    {displayPercentile !== null ? (
                      <>
                        <div className="metric-value" style={{
                          fontSize: '2.5rem',
                          color: displayExceeded ? 'var(--color-danger)' : displayPercentile > 50 ? 'var(--color-warning)' : 'var(--color-success)'
                        }}>
                          {isEstimated && '~'}
                          {displayPercentile >= 95 ? 'Top ' + (100 - displayPercentile) : displayPercentile.toFixed(0)}%
                        </div>
                        <div className="metric-subtitle" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', justifyContent: 'center' }}>
                          Percentile
                          {isBackendEstimated && (
                            <span className="badge-modern badge-info" style={{
                              fontSize: '0.625rem',
                              padding: '0.125rem 0.375rem',
                              background: '#efebe1',
                              color: '#34312c',
                              fontWeight: 600
                            }}>
                              EST.
                            </span>
                          )}
                        </div>
                        {isBackendEstimated && basic.estimationReason && (
                          <div style={{
                            marginTop: 'var(--space-sm)',
                            fontSize: '0.6875rem',
                            color: 'var(--color-gray-500)',
                            fontStyle: 'italic',
                            textAlign: 'center'
                          }}>
                            {basic.estimationReason as React.ReactNode}
                          </div>
                        )}
                      </>
                    ) : basic.eliminated ? (
                      <>
                        <div className="badge-modern badge-neutral">Insufficient Data</div>
                        <div className="metric-subtitle" style={{ marginTop: 'var(--space-sm)', fontStyle: 'italic' }}>
                          {(basic.eliminationReason as React.ReactNode) || 'Not enough data to score'}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="badge-modern badge-neutral">Not Public</div>
                        <div className="metric-subtitle" style={{ marginTop: 'var(--space-sm)' }}>Hidden by FMCSA</div>
                      </>
                    )}
                  </div>

                  {/* Violations */}
                  <div className="stat-box">
                    <div className="stat-value" style={{ fontSize: '1.25rem' }}>{(basic.totalViolation || 0) as React.ReactNode}</div>
                    <div className="stat-label">Violations</div>
                  </div>

                  {/* Inspections */}
                  <div className="stat-box">
                    <div className="stat-value" style={{ fontSize: '1.25rem' }}>{(basic.totalInspectionWithViolation || 0) as React.ReactNode}</div>
                    <div className="stat-label">Inspections</div>
                  </div>

                  {/* Threshold */}
                  <div className="stat-box">
                    <div className="stat-value" style={{ fontSize: '1.25rem', color: 'var(--color-gray-600)' }}>{threshold}%</div>
                    <div className="stat-label">Threshold</div>
                    {displayPercentile !== null && (
                      <div className={`badge-modern ${displayExceeded ? 'badge-danger' : 'badge-success'}`} style={{ marginTop: 'var(--space-sm)' }}>
                        {displayExceeded ? 'Exceeded' : 'Below'}
                      </div>
                    )}
                  </div>
                </div>
                {isExpanded && (
                  <BasicHistoryChart
                    dotNumber={profile.dotNumber}
                    basicId={canonicalBasicId}
                    basicName={basicsShortDesc ? decodeHTMLEntities(basicsShortDesc) : basicsCode}
                    threshold={threshold}
                  />
                )}
              </div>
            );
          })}
        </>
      ) : (
        <div className="card-modern" style={{ textAlign: 'center', padding: 'var(--space-lg)' }}>
          <Award style={{ width: 40, height: 40, color: 'var(--color-gray-300)', margin: '0 auto var(--space-lg)' }} />
          <p className="text-muted" style={{ fontSize: '1rem' }}>No BASIC score data available for this carrier.</p>
        </div>
      )}
    </div>
  );
};

export default Basics;
